import React, { useRef, useCallback } from 'react';
import {
  Table as ShadcnTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'app/components/ui/table';
import { Loader } from 'lucide-react';

export type TableColumn<T> = {
  key: string;
  header: string | (() => React.ReactNode);
  render?: (row: T) => React.ReactNode;
  className?: string;
  width?: string;
  responsiveWidth?: string;
};

export type TableProps<T> = {
  columns: TableColumn<T>[];
  data: T[];
  isLoading: boolean;
  isError?: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  rowKey: (row: T) => string | number;
  emptyMessage?: string;
  className?: string;
  onRowClick?: (row: T) => void;
  selectedId?: string | number | null;
  tableHeight?: string;
  selectedStyles?: string;
  headerHeight?: string;
  headerPadding?: string;
  firstHeaderPadding?: string;
  lastHeaderPadding?: string;
  headerStyles?: string;
  tableBodyStyles?: string;
};

export function Table<T>({
  columns,
  data,
  isLoading,
  isError,
  hasMore,
  onLoadMore,
  rowKey,
  emptyMessage = 'No data found',
  className = '',
  onRowClick,
  selectedId,
  tableHeight = 'h-[calc(100vh-14.5rem)]',
  selectedStyles = 'bg-[#DDE1E680] hover:bg-[#DDE1E690] border-l-2',
  headerHeight = 'h-[3.375rem]',
  headerPadding = 'px-1.5',
  firstHeaderPadding = 'pl-4',
  lastHeaderPadding = 'pr-4',
  headerStyles = 'bg-[#DDE1E6] border-l border-l-[#DDE1E6]',
  tableBodyStyles = '[&_tr:last-child]:border-b',
}: TableProps<T>) {
  // Ensure data is always an array
  const safeData = Array.isArray(data) ? data : [];
  const observer = useRef<IntersectionObserver | null>(null);
  const lastRowRef = useCallback(
    (node: HTMLTableRowElement | null) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new window.IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore) {
          onLoadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore, onLoadMore]
  );

  return (
    <div className={`overflow-hidden bg-white rounded-lg responsive-table ${className}`}>
      <div className={`sticky top-0 z-10 ${headerStyles}`}>
        <ShadcnTable className="w-full">
          <TableHeader className={`${headerHeight} [&_tr]:border-b-0`}>
            <TableRow className="hover:bg-transparent">
              {columns?.map((col, index) => (
                <TableHead 
                  key={col.key}
                  className={`${col.className} ${headerPadding} ${
                    index === 0 ? firstHeaderPadding : 
                    index === columns.length - 1 ? lastHeaderPadding : 
                    ''
                  }`}
                  style={{
                    width: col.width,
                    minWidth: col.width,
                    maxWidth: col.width,
                    '--responsive-width': col.responsiveWidth || col.width,
                  } as React.CSSProperties}
                >
                  {typeof col.header === 'function' ? col.header() : col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
        </ShadcnTable>
      </div>
      <div className={`overflow-y-auto custom-scrollbar ${tableHeight}`}>
        <ShadcnTable className="w-full">
          <TableBody className={`${tableBodyStyles}`}>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className={`text-center text-[#343A3F] text-sm font-medium leading-[0.75rem] tracking-[-0.00625rem] ${tableHeight}`}
                >
                  <div className="flex flex-col gap-2 justify-center items-center">
                    <Loader className="animate-spin" /> Loading appointments...
                  </div>
                </TableCell>
              </TableRow>
            ) : safeData.length === 0 ? (
              <TableRow>
                <TableCell 
                  colSpan={columns.length} 
                  className={`text-center text-[#343A3F] text-sm font-medium leading-[0.75rem] tracking-[-0.00625rem] ${tableHeight}`}
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              <>
                {safeData.map((row, i) => {
                  const isLast = i === safeData.length - 1;
                  const rowId = rowKey(row);
                  return (
                    <TableRow
                      key={rowId}
                      ref={isLast ? lastRowRef : undefined}
                      data-row-id={rowId}
                      className={`cursor-pointer ${
                        selectedId === rowId 
                          ? `${selectedStyles} border-l-[#5B73F1]` 
                          : 'hover:bg-[#DDE1E640]'
                      }`}
                      onClick={() => onRowClick?.(row)}
                      tabIndex={0}
                      aria-label={`Select row ${rowId}`}
                    >
                      {columns.map((col, index) => (
                        <TableCell 
                          key={col.key}
                          className={`${col.className} ${headerPadding} ${
                            index === 0 ? firstHeaderPadding : 
                            index === columns.length - 1 ? lastHeaderPadding : 
                            ''
                          }`}
                          style={{
                            width: col.width,
                            minWidth: col.width,
                            maxWidth: col.width,
                            '--responsive-width': col.responsiveWidth || col.width,
                          } as React.CSSProperties}
                        >
                          {col.render ? col.render(row) : (row as any)[col.key]}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
                {hasMore && isLoading && (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="py-4 text-center"
                    >
                      <div className="flex justify-center items-center">
                        <Loader className="mr-2 w-4 h-4 animate-spin" />
                        <span className="text-sm text-gray-600">Loading more appointments...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </ShadcnTable>
      </div>
    </div>
  );
};
